import { IOrderClause } from '@tableau/data-catalog-client-components';

import { Location } from '~common/react/Location';
import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { getAppStore } from '~src/app/AppStore';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { Messages } from '~src/codegen/Localize';
import { RestApiListResourceFetcher } from '~src/commonInfra/utils/resourceFetchers/RestApiListResourceFetcher';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { IActionResource, IListResource } from '~src/ts/resources/Resource';
import { ResourceQuery } from '~src/ts/resources/ResourceQuery';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { ConnectedApplicationMenuActions } from '../actions/ConnectedApplicationActions';
import { IConnectedApplicationInfo, localizeConnectedApplicationDomainSafelist } from '../models/ConnectedApplicationInfo';
import { ConnectedApplicationInfoMapper } from '../models/ConnectedApplicationInfoMapper';
import { callCreateConnectedApplication, CreateConnectedApplicationResult, CreateConnectedApplicationType } from '../serverApi/requests/CreateConnectedApplication';
import { callCreateConnectedApplicationsSecret, CreateConnectedApplicationSecretResult } from '../serverApi/requests/CreateSecret';
import { callDeleteConnectedApplication } from '../serverApi/requests/DeleteConnectedApplication';
import { callDeleteConnectedApplicationsSecret } from '../serverApi/requests/DeleteSecret';
import { callGetConnectedApplications, IGetConnectedApplicationsResult } from '../serverApi/requests/GetConnectedApplications';
import { callGetConnectedApplicationsSecret, IGetConnectedApplicationsSecretResult } from '../serverApi/requests/GetSecretValueById';
import { callUpdateConnectedApplication, UpdateConnectedApplicationResult, UpdateConnectedApplicationType } from '../serverApi/requests/UpdateConnectedApplication';

function connectedApps(connectedAppsResult: IGetConnectedApplicationsResult): IConnectedApplicationInfo[] {
  if (connectedAppsResult.connectedApplications.connectedApplication)
    return ConnectedApplicationInfoMapper.map(connectedAppsResult.connectedApplications.connectedApplication);
  return [];
}

export type ConnectedApplicationsResultMapper = (connectedAppsResult: IGetConnectedApplicationsResult) => IConnectedApplicationInfo[];

class ConnectedApplicationsResource implements IListResource<IConnectedApplicationInfo>, IActionResource<ConnectedApplicationMenuActions> {

  public all(order?: IOrderClause[]): ResourceQuery<IConnectedApplicationInfo> {
    const fetcher = new RestApiListResourceFetcher(callGetConnectedApplications, this.getResultMapper(order));
    return new ResourceQuery(fetcher);
  }

  public mapFieldToValue(connectedApplication: IConnectedApplicationInfo, field: keyof IConnectedApplicationInfo): string | number {
    // We want to avoid sorting on the underlying boolean/id fields since the user can't see them and the sort results would make less sense
    switch(field) {
      case 'enabled':
        return connectedApplication.enabled
          ? Messages.connectedApplicationsEnabled()
          : Messages.connectedApplicationsDisabled();
      case 'projectId':
        return connectedApplication.projectId
          ? Messages.connectedApplicationsScopeSingleProject()
          : Messages.connectedApplicationsScopeAllProjects();
      case 'domainSafelistForGrid':
        return localizeConnectedApplicationDomainSafelist(connectedApplication.domainSafelistForGrid);
      default:
        return connectedApplication[field];
    }
  }

  public getResultMapper(orderClauses?: IOrderClause[]): ConnectedApplicationsResultMapper {
    return result => {
      let items = connectedApps(result);
      if(orderClauses) {
        // If A > B and sorting ascending then return 1
        items.sort((a,b) => {
          for(let clause of orderClauses) {
            const direction = clause.ascending ? 1 : -1;
            const aVal = this.mapFieldToValue(a, clause.field as keyof IConnectedApplicationInfo);
            const bVal = this.mapFieldToValue(b, clause.field as keyof IConnectedApplicationInfo);
            if (aVal !== bVal) {
              return aVal > bVal ? direction : -direction;
            }
            // If the fields are equal in value we continue to the next one until
            // we either hit a tie-breaker or run out of fields to compare
          }
          return 0;
        });
      }
      return items;
    };
  }

  // This is needed because we use the generic infra for handling delete menu and it
  // doesn't know when to enable or disable. Other types make an actual web request
  // but if you can see it, you can delete it for ConnectedApps
  public actions(): Promise<ConnectedApplicationMenuActions> {
    return Promise.resolve({
      del: true
    });
  }

  public del(ids: string[]): Promise<IResult> {
    const deleteCalls = ids.map(id => callDeleteConnectedApplication(id));
    return Promise.all(deleteCalls).then(ResultHelper.combineResults);
  }

  public async create(params: CreateConnectedApplicationType): Promise<CreateConnectedApplicationResult> {
    try {
      const result = await callCreateConnectedApplication({
        connectedApplication: params,
      });
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.connectedApplicationsConnectedApplicationCreated(),
      });
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.errorUnknown(),
      });
    }
  }

  public async update(params: UpdateConnectedApplicationType): Promise<UpdateConnectedApplicationResult> {
    try {
      const result = await callUpdateConnectedApplication({
        connectedApplication: params,
      });
      if(result !== undefined && !result.error) {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.connectedApplicationsConnectedApplicationUpdated(),
        });
      }
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.errorUnknown(),
      });
    }
  }

  public navigateToAllConnectedApplicationsPage() {
    const urls = siteUrlsSelector(getAppStore().getState());
    Location.instance.setUrl(urls.settingsConnectedApplicationsUrl());
    getAppStore().dispatch(requestSpaceContents());
  }

  public async deleteSecret(appId: string, secretId: string): Promise<IResult> {
    try {
      const result = await callDeleteConnectedApplicationsSecret(appId, secretId);
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.connectedApplicationsDeleteSecretSuccessMessage(),
      });
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.connectedApplicationsDeleteSecretFailureMessage(),
      });
    }
  }

  public async getSecret(appId: string, secretId: string): Promise<IGetConnectedApplicationsSecretResult> {
    try {
      const result = await callGetConnectedApplicationsSecret(appId, secretId);
      return result;
    } catch (e) {
    }
  }

  public async createSecret(appId: string): Promise<CreateConnectedApplicationSecretResult> {
    try {
      const result = await callCreateConnectedApplicationsSecret(appId);
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.connectedApplicationsGenerateSecretSuccessMessage(),
      });
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.connectedApplicationsGenerateSecretFailureMessage(),
      });
    }
  }

  public navigateToConnectedApplicationsDetailsPage(id: string) {
    const urls = siteUrlsSelector(getAppStore().getState());
    Location.instance.setUrl(urls.connectedApplicationUrl(id));
  }
}

export const ConnectedApplications = new ConnectedApplicationsResource();
